import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { useWorkspace } from './WorkspaceContext';
import { calendarKeys } from '@/pages/calendar/calendarQueries';
import { setCalendarV2ActiveWorkspace } from '@/services/calendarV2Api';
import { setActiveCalendarDirtyLabels } from './calendarDirtyRegistry';
import { useCalendarRealtime } from '@/pages/calendar/calendarRealtime';

type RegisterDirty = (key: string, dirty: boolean, label: string) => void;
interface CalendarRuntimeValue { workspaceId: string; registerDirty: RegisterDirty; realtimeStatus: 'connecting' | 'connected' | 'disconnected' }
const CalendarRuntimeContext = createContext<CalendarRuntimeValue | null>(null);

export const CalendarRuntimeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const client = useQueryClient(); const location = useLocation(); const requested = useWorkspace().currentWorkspace?.id ?? '';
  const normalizedPathname = location.pathname.replace(/\/+$/, '') || '/';
  const activeRoute = normalizedPathname === '/calendar' || normalizedPathname.startsWith('/calendar/'); const previous = useRef(requested); const [ready, setReady] = useState(requested);
  const [, setDirty] = useState<Record<string, string>>({});
  const registerDirty = useCallback<RegisterDirty>((key, value, label) => setDirty(current => {
    if (value && current[key] === label) return current; if (!value && !(key in current)) return current;
    const next = { ...current }; if (value) next[key] = label; else delete next[key];
    setActiveCalendarDirtyLabels(Array.from(new Set(Object.values(next)))); return next;
  }), []);
  const workspaceId = activeRoute && ready === requested ? requested : '';
  const realtime = useCalendarRealtime(workspaceId);

  useLayoutEffect(() => {
    let mounted = true;
    const switchWorkspace = async () => {
      const old = previous.current; setReady(''); setCalendarV2ActiveWorkspace('');
      if (old && old !== requested) { await client.cancelQueries({ queryKey: calendarKeys.root(old) }); client.removeQueries({ queryKey: calendarKeys.root(old) }); }
      client.removeQueries({ predicate: query => ['calendar', 'calendar-feed', 'calendar-groups', 'calendar-users'].includes(String(query.queryKey[0])) });
      previous.current = requested; if (mounted) { setCalendarV2ActiveWorkspace(requested); setReady(requested); }
    };
    void switchWorkspace(); return () => { mounted = false; };
  }, [client, requested]);
  useEffect(() => () => setActiveCalendarDirtyLabels([]), []);

  return <CalendarRuntimeContext.Provider value={{ workspaceId, registerDirty, realtimeStatus: realtime.status }}>
    {children}
  </CalendarRuntimeContext.Provider>;
};
export const useCalendarRuntime = (): CalendarRuntimeValue => { const value = useContext(CalendarRuntimeContext); if (!value) throw new Error('useCalendarRuntime must be used within CalendarRuntimeProvider'); return value; };
export const useCalendarDirtyGuard = (key: string, dirty: boolean, label: string): void => { const { registerDirty } = useCalendarRuntime(); useEffect(() => { registerDirty(key, dirty, label); return () => registerDirty(key, false, label); }, [dirty, key, label, registerDirty]); };
